import { Radio } from "antd";
import {
  chemotherapySessionsV2,
  FormPayloadV2,
} from "../../data/drugOptionsV2";
import SelectDiseaseV2 from "../SelectDisease/SelectDiseaseV2";
import Button from "../../atoms/Button/Button";
import Popup from "../../atoms/Popup/Popup";
import CycleOrSessionDetailsV2 from "./CycleOrSessionDetailsV2";
import useScheduleCycleV2 from "./useScheduleCycleV2";
import Input from "../../atoms/Input/Input";
import { useMemo, forwardRef, useImperativeHandle } from "react";

export type CreateCycleOrSessionV2Handle = {
  /** Merge OCR field values into the schedule form (prefill). */
  applyOcr: (data: Record<string, string>) => void;
};

interface CreateCycleOrSessionV2Props {
  showUploadDocuments: (formData: FormPayloadV2) => void;
  detailsObject: any;
}

const CreateCycleOrSessionV2 = forwardRef<
  CreateCycleOrSessionV2Handle,
  CreateCycleOrSessionV2Props
>(function CreateCycleOrSessionV2({ showUploadDocuments, detailsObject }, ref) {
  const {
    formData,
    isLoading,
    isModalOpen,
    setFormData,
    scheduleCycle,
    setIsModalOpen,
    handleTypeChange,
    handleAddMoreDetails,
    applyOcrToScheduleForm,
  } = useScheduleCycleV2({ showUploadDocuments, detailsObject });

  useImperativeHandle(
    ref,
    () => ({
      applyOcr: applyOcrToScheduleForm,
    }),
    [applyOcrToScheduleForm],
  );

  const handleCompletedCycles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // Allow clearing the field
    if (value === "") {
      setFormData({
        ...formData,
        cycles_completed: "" as any,
      });
      return;
    }

    const parsed = parseInt(value, 10);

    // Block 0 explicitly
    if (parsed === 0 || Number.isNaN(parsed)) {
      return;
    }

    setFormData({
      ...formData,
      cycles_completed: parsed,
    });
  };

  // Validation for Create Cycle button in modal
  const isCreateCycleDisabled = useMemo(() => {
    // Basic required fields
    if (
      !formData.primaryDrug ||
      !formData.cancerType ||
      !formData.therapy ||
      !formData.cycle_number ||
      !formData.upcoming_cycle_date ||
      !formData.notes
    ) {
      return true;
    }

    // Check indication ONLY if the therapy has indications available
    // For Tucatinib MBC therapies, indications array is empty, so skip
    // We skip the indication check for therapies that start with "MBC" for Tucatinib
    if (
      formData.primaryDrug === "tucatinib" &&
      formData.therapy?.startsWith("MBC")
    ) {
      // MBC therapy (including MBC 1st line, 2nd line, 3rd line) has no indications
    } else if (formData.primaryDrug === "nivolumab") {
      // For Nivolumab, indication is required if available
      if (!formData.indication) {
        return true;
      }
    } else if (formData.primaryDrug === "tucatinib") {
      // For non-MBC Tucatinib therapies, check if indication is required
      if (!formData.indication) {
        console.log(
          "Validation FAILED - Non-MBC Tucatinib indication required",
        );
        return true;
      }
    }

    // For Nivolumab, dosage is required
    if (formData.primaryDrug === "nivolumab" && !formData.upcoming_dosage) {
      return true;
    }

    return false;
  }, [formData]);

  // Validation for "Upload documents" or "Create Current Cycle/Session" button
  const isAddMoreDetailsDisabled = useMemo(() => {
    return (
      !formData?.primaryDrug ||
      !(
        formData?.primaryDrugPaidStatus === 0 ||
        formData?.primaryDrugPaidStatus === 1
      ) || // Primary drug paid status is required
      !formData?.cancerType ||
      !formData?.cycle_number ||
      (formData?.cycle_number === "1" && !formData?.cycles_completed) || // If cycle number is 1 (bulk creation), cycles completed is required
      (formData?.secondaryDrugs &&
        formData?.secondaryDrugs?.length > 0 &&
        formData?.secondaryDrugs?.some(
          (drug) =>
            !drug?.drug_name || !(drug?.is_free === 0 || drug?.is_free === 1),
        )) // If secondary drugs are selected, drug name is required and paid status is required
    );
  }, [formData]);

  return (
    <div className="w-full">
      {/* Drug Selection Section */}
      <SelectDiseaseV2 formData={formData} setFormData={setFormData} />

      {/* Chemotherapy Sessions Section */}
      <div className="w-full mt-4">
        <p className="text-bold mt-0 mb-2">
          How many chemotherapy sessions has the user completed?
        </p>
        <Radio.Group
          onChange={(e) => handleTypeChange(e)}
          value={formData.cycle_number}
          className="custom-radio"
        >
          {chemotherapySessionsV2.map((type: Record<string, string>) => (
            <Radio value={type.value} className="input-label" key={type.value}>
              {type.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>

      {/* Completed Cycles Input (for "1 or more" option) */}
      {formData.cycle_number === "1" &&
        formData.cancerType &&
        formData.primaryDrug &&
        formData.cycle_number && (
          <div className="w-full mt-4">
            <div className="max-w-lg mb-2">
              <p className="input-label mb-1">
                Enter number of sessions/cycles completed
              </p>
              <p className="text-gray-500 text-sm m-0">
                Note:- Only include chemo sessions that have already been
                completed. Don't count the upcoming session that hasn't started
                yet.
              </p>
            </div>
            <div className="max-w-md">
              <Input
                id="cycle_number"
                value={formData.cycles_completed}
                onChange={handleCompletedCycles}
                placeholder="Enter number of sessions/cycles"
                type="number"
                name="cycles_completed"
              />
            </div>
          </div>
        )}

      {/* Action Button */}
      <div className="py-4 max-content mt-4">
        <Button
          fullWidth
          disabled={isAddMoreDetailsDisabled}
          onClick={handleAddMoreDetails}
        >
          {formData.cycle_number === "1"
            ? "Upload documents"
            : "Create Current Cycle/Session"}
        </Button>
      </div>

      {/* Modal for Cycle/Session Details */}
      <Popup
        show={isModalOpen}
        headerText="Create Current Cycle/Session"
        onClose={() => setIsModalOpen(false)}
        size="md"
      >
        <div className="preview-modal">
          <CycleOrSessionDetailsV2
            formData={formData}
            setFormData={setFormData}
          />

          <div className="popup-footer">
            <Button
              onClick={scheduleCycle}
              disabled={isCreateCycleDisabled}
              loading={isLoading}
            >
              Create Cycle/Session
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  );
});

export default CreateCycleOrSessionV2;
